import type { IncidentPriority } from "../../types/incident";

/** Backend IncidentPriority enum values */
export const INCIDENT_PRIORITIES = [
  "CRISIS",
  "CRITICAL",
  "HIGH",
  "MEDIUM",
  "LOW",
] as const satisfies readonly IncidentPriority[];

const INCIDENT_PRIORITY_ALIASES: Record<string, IncidentPriority> = {
  CRISIS: "CRISIS",
  CRITICAL: "CRITICAL",
  HIGH: "HIGH",
  MEDIUM: "MEDIUM",
  LOW: "LOW",
  Crisis: "CRISIS",
  Critical: "CRITICAL",
  High: "HIGH",
  Medium: "MEDIUM",
  Low: "LOW",
};

export function normalizeIncidentPriority(
  priority?: string | null,
): IncidentPriority | null {
  if (!priority) return null;
  return (
    INCIDENT_PRIORITY_ALIASES[priority] ??
    INCIDENT_PRIORITY_ALIASES[priority.toUpperCase()] ??
    null
  );
}

/** Coerce any priority input to a backend enum value before API calls. */
export function toIncidentPriorityEnum(
  priority: string | null | undefined,
): IncidentPriority {
  const normalized = normalizeIncidentPriority(priority);
  if (normalized) {
    return normalized;
  }
  throw new Error(
    `Invalid incident priority "${priority}". Expected one of: ${INCIDENT_PRIORITIES.join(", ")}`,
  );
}

export const INCIDENT_PRIORITY_OPTIONS: {
  value: IncidentPriority;
  label: string;
  icon: string;
  i18nKey: string;
}[] = [
  { value: "CRISIS", label: "Crisis", icon: "emergency", i18nKey: "options.priority.CRISIS" },
  { value: "CRITICAL", label: "Critical", icon: "error", i18nKey: "options.priority.CRITICAL" },
  { value: "HIGH", label: "High", icon: "priority_high", i18nKey: "options.priority.HIGH" },
  { value: "MEDIUM", label: "Medium", icon: "remove", i18nKey: "options.priority.MEDIUM" },
  { value: "LOW", label: "Low", icon: "south", i18nKey: "options.priority.LOW" },
];

export function getIncidentPriorityLabel(priority?: string | null): string {
  const normalized = normalizeIncidentPriority(priority);
  if (normalized) {
    return (
      INCIDENT_PRIORITY_OPTIONS.find((o) => o.value === normalized)?.label ??
      normalized
    );
  }
  return priority?.trim() || "N/A";
}
